import { HttpStatus } from '@/errors/http-status';
import { ChatSessions } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { IAgentKey } from '@d-id/types';
import { AxiosError } from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import { getOrCreateAgentClientKey } from '../agent';

export const CLIENT_KEY = 'CLIENT_KEY';
let freeAgentSessionsDone = false;

export function useChatSessionsQuery() {
    const { isAnonymous = true } = useAuth();
    const queryClient = useQueryClient();

    return useQuery<IAgentKey[], AxiosError, ChatSessions>(CLIENT_KEY, getOrCreateAgentClientKey, {
        placeholderData: [{ chat_sessions: 201, expire_at: '' }],
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: (failureCount, error) => {
            if ((error as AxiosError)?.response?.status === HttpStatus.MAX_ALLOWED_DOMAINS_LIMIT_REACHED) {
                return false;
            }
            return failureCount < 5;
        },
        enabled: !isAnonymous,

        retryDelay: (attemptIndex, error) => {
            if ((error as AxiosError)?.response?.status === 404) {
                return 0;
            }
            return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
        select: (data: IAgentKey[]): ChatSessions => {
            const total = Number(data?.[0]?.chat_sessions ?? 201);
            const freeLeft = Math.max(200 - total, 0);
            const expire_at = (data?.[0]?.expire_at ?? '') as string;
            const isExpired = new Date(expire_at) < new Date();

            return {
                total,
                freeLeft,
                expire_at,
                isExpired,
            };
        },
        onSuccess: (data: ChatSessions) => {
            if (!(data.isExpired || data.freeLeft === 0)) {
                freeAgentSessionsDone = false;
            } else if (!freeAgentSessionsDone) {
                freeAgentSessionsDone = true;
            }
        },
        onError: err => {
            const axiosError = err as AxiosError;
            const prevData = queryClient.getQueryData<ChatSessions>(CLIENT_KEY);
            queryClient.setQueryData(CLIENT_KEY, prevData ?? { freeLeft: 0, total: 201, expire_at: '', isExpired: false });
            // Only send to sentry if get client key is valid error
            if ((axiosError as AxiosError)?.response?.status !== 404) {
                logger.error('Could not fetch client key', { err, status: axiosError.response?.status });
            }
        },
    });
}
